<!-- SPDX-License-Identifier: AGPL-3.0-only -->

<script setup lang="ts">
import { computed, onMounted, ref, watch, type ComputedRef, type Ref } from "vue";
import {
  getTenantFirmwarePolicy,
  updateTenantFirmwarePolicy,
  type FirmwareChannel,
  type FirmwareUpdateMode,
  type TenantFirmwarePolicy,
} from "../api/firmware.js";
import type { TenantSummary } from "../api/tenants.js";
import AppShell from "../components/AppShell.vue";
import ProbePageTabs from "../components/ProbePageTabs.vue";
import { formatDateTime } from "../formatting/date-time.js";
import { useTenantStore } from "../stores/tenants.js";
import { useUserSettingsStore } from "../stores/user-settings.js";

const tenantStore: ReturnType<typeof useTenantStore> = useTenantStore();
const userSettingsStore: ReturnType<typeof useUserSettingsStore> = useUserSettingsStore();
const tenant: ComputedRef<TenantSummary | null> = computed(
  (): TenantSummary | null => tenantStore.selectedTenant,
);
const policy: Ref<TenantFirmwarePolicy | null> = ref(null);
const defaultChannel: Ref<FirmwareChannel> = ref("stable");
const defaultUpdateMode: Ref<FirmwareUpdateMode> = ref("manual");
const loading: Ref<boolean> = ref(false);
const saving: Ref<boolean> = ref(false);
const message: Ref<string | null> = ref(null);
const failed: Ref<boolean> = ref(false);

const canExecuteProbeOperations: ComputedRef<boolean> = computed(
  (): boolean => tenant.value?.access.capabilities.includes("probe.operations.execute") === true,
);

function errorText(error: unknown): string {
  return error instanceof Error ? error.message : "Firmware rollout policy request failed.";
}

function applyPolicy(nextPolicy: TenantFirmwarePolicy): void {
  policy.value = nextPolicy;
  defaultChannel.value = nextPolicy.defaultChannel;
  defaultUpdateMode.value = nextPolicy.defaultUpdateMode;
}

async function loadPolicy(): Promise<void> {
  const currentTenant: TenantSummary | null = tenant.value;
  if (currentTenant === null) {
    policy.value = null;
    return;
  }
  loading.value = true;
  message.value = null;
  failed.value = false;
  try {
    applyPolicy(await getTenantFirmwarePolicy(currentTenant.id));
  } catch (error: unknown) {
    failed.value = true;
    message.value = errorText(error);
  } finally {
    loading.value = false;
  }
}

async function savePolicy(): Promise<void> {
  const currentTenant: TenantSummary | null = tenant.value;
  if (currentTenant === null || saving.value || !canExecuteProbeOperations.value) {
    return;
  }
  saving.value = true;
  message.value = null;
  failed.value = false;
  try {
    applyPolicy(await updateTenantFirmwarePolicy(
      currentTenant.id,
      defaultChannel.value,
      defaultUpdateMode.value,
    ));
    message.value = "Firmware rollout defaults saved.";
  } catch (error: unknown) {
    failed.value = true;
    message.value = errorText(error);
  } finally {
    saving.value = false;
  }
}

onMounted(loadPolicy);
watch((): string | null => tenant.value?.id ?? null, loadPolicy);
</script>

<template>
  <AppShell v-if="tenant">
    <header class="page-heading">
      <div>
        <p class="eyebrow">{{ tenant.slug }}</p>
        <h1>Probe firmware rollout</h1>
        <p>Define the tenant default firmware channel and rollout mode. Individual probes can override these values from Inventory maintenance details.</p>
      </div>
    </header>

    <ProbePageTabs />

    <p v-if="message" :class="failed ? 'form-error' : 'settings-message'" :role="failed ? 'alert' : 'status'">
      {{ message }}
    </p>
    <p v-if="loading" class="placeholder-panel">Loading firmware rollout policy…</p>

    <section v-else class="content-panel settings-card">
      <div class="settings-card__heading">
        <div>
          <p class="eyebrow">Tenant defaults</p>
          <h2>Rollout policy</h2>
        </div>
        <span v-if="policy" class="status-badge status-badge--active">
          {{ policy.defaultChannel }} / {{ policy.defaultUpdateMode }}
        </span>
      </div>
      <form class="settings-form tenant-firmware-policy-form" @submit.prevent="savePolicy">
        <label>
          <span>Default channel</span>
          <select v-model="defaultChannel" :disabled="!canExecuteProbeOperations">
            <option value="stable">Stable</option>
            <option value="beta">Beta</option>
          </select>
        </label>
        <label>
          <span>Default update mode</span>
          <select v-model="defaultUpdateMode" :disabled="!canExecuteProbeOperations">
            <option value="manual">Manual · button required per probe</option>
            <option value="automatic">Automatic · instruct on next report</option>
            <option value="disabled">Disabled · block all update instructions</option>
          </select>
        </label>
        <p class="notice settings-field--wide">
          A probe updates only within its effective channel. Per-probe overrides can inherit these defaults or explicitly select channel and update mode.
        </p>
        <p v-if="policy" class="settings-message settings-field--wide">
          Last updated {{ formatDateTime(policy.updatedAt, userSettingsStore.settings.dateTimeFormat) }}.
        </p>
        <button
          v-if="canExecuteProbeOperations"
          class="button button--primary settings-field--wide"
          type="submit"
          :disabled="saving"
        >
          {{ saving ? "Saving…" : "Save rollout defaults" }}
        </button>
      </form>
    </section>
  </AppShell>
</template>
